'use client'
import Link from "next/link";
import useMount from "react-use/lib/useMount";

import styles from './destination.module.css';
import buttons from "components/_styled/buttons.module.css";
import wrappers from "components/_styled/wrappers.module.css";

import Loading from "components/Loading";
import LazyImage from "components/LazyImage";

import logInternalLinkClick from "lib/ga/internalLinkClick";
import logPageView from "lib/ga/pageView";
const DestinationPage = (props) => {
  if (props.isNotBid && (!props.destination || !props.destination.name)) return <Loading />;
  const {
    name,
    uri,
    location,
    descShort,
    descLong,
    description,
    stats,
    heroImage,
  } = props.destination;
  const ImageURL = (format: string, width: string, retina: boolean = false) =>
    `${process.env.NEXT_PUBLIC_APP_CDN_URL}destinations/${uri}_hero-${width}${retina ? "@2x" : ""}.${format}`;

  useMount(() => {
    logPageView({ url: window.location.pathname + window.location.search });
  });
  return (
      <div className={wrappers.FluidWrapper}>
        <div className={styles.LinkBanner}>
          <div className={wrappers.SmoothWrapper}>
            <Link href="/destinations" prefetch={false} passHref legacyBehavior>
              <a onClick={() => logInternalLinkClick("Back to Destinations", "/destinations")}>
                &#8592; Back to Destinations
              </a>
            </Link>
          </div>
        </div>
        <div className={styles.Details}>
          <h1 className={styles.Title}>Retail Property {name} Guide</h1>
          <h3 className={styles.SubTitle}>{descShort}</h3>
          <figure style={{ marginBottom: "10px" }}>
            <picture>
              <source
                media="(max-width: 500px)"
                srcSet={`${ImageURL("webp", "h325")} 1x, ${ImageURL("webp", "h325", true)} 2x`}
                type="image/webp"
              />
              <source
                srcSet={`${ImageURL("webp", "w970")} 1x, ${ImageURL("webp", "w970", true)} 2x`}
                type="image/webp"
              />
              <img srcSet={`${ImageURL("jpg", "w970")} 1x, ${ImageURL("jpg", "w970", true)} 2x`} alt={name} />
            </picture>
            {/* <LazyImage
              src={heroImage.src}
              alt={name}
              layout="responsive"
              width={970}
              height={970 * (heroImage.height / heroImage.width)}
            /> */}
            {heroImage.caption && <figcaption>{heroImage.caption}</figcaption>}
          </figure>
          <div className={styles.Body}>
            {description ? (
              <section className={styles.Description}>
                {description.map((desc, key) => {
                  let { internalLink, href, ...attributes } = desc.attributes;
                  switch (desc.tag) {
                    case "h3":
                      return <h3 key={key} {...attributes} dangerouslySetInnerHTML={{ __html: desc.value }} />;
                    case "h4":
                      return <h4 key={key} {...attributes} dangerouslySetInnerHTML={{ __html: desc.value }} />;
                    case "p":
                      return <p key={key} {...attributes} dangerouslySetInnerHTML={{ __html: desc.value }} />;
                    case "img":
                      return (
                        <figure key={key}>
                          <LazyImage {...attributes} />
                          {desc.value && <figcaption dangerouslySetInnerHTML={{ __html: desc.value }} />}
                        </figure>
                      );
                    case "ul":
                      return (
                        <ul key={key} {...attributes}>
                          {desc.value.map((val) => (
                            <li key={`li_${val.length}`}>{val}</li>
                          ))}
                        </ul>
                      );
                    case "a":
                      if (internalLink) {
                        return (
                          <Link key={key} href={href} passHref prefetch={false} legacyBehavior>
                            <a {...attributes} className={buttons.SecondaryButton}>
                              {desc.value}
                            </a>
                          </Link>
                        );
                      }

                      return (
                        <a key={key} {...attributes} href={href} className={buttons.SecondaryButton}>
                          {desc.value}
                        </a>
                      );
                  }
                })}
              </section>
            ) : (
              <p className={styles.ShortDescription} dangerouslySetInnerHTML={{ __html: descLong }} />
            )}

            {stats && (
              <div>
                {stats.map((stat) => (
                  <div className={styles.Stat} key={stat.title}>
                    <strong>{stat.title}</strong>
                    <p>{stat.value}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        {props.schemes && props.schemes.length > 0 && (
          <div className={`${styles.FeaturedPicks} ${styles.Schemes}`}>
            <h2>Find your next shop to let in {name}</h2>
            <p>
              Renting a unit within a retail scheme is a great way to gain exposure for your brand because they attract
              a large footfall of customers who wish to engage with retail, leisure and culinary providers.
            </p>
            <div className={`${styles.Tiles} ${styles.SchemesDiv}`}>
              {props.schemes.map((scheme) => {
                let address = [scheme.line1 === scheme.full_name ? null : scheme.line1, scheme.line2, scheme.line3, scheme.town_name].filter(Boolean).filter(value => Object.keys(value).length !== 0).filter(Boolean).join(", ");
                address = [address, scheme.postcode].filter(Boolean).join(", ");
                return (
                  <Link key={scheme.id.raw} href={`/scheme/${scheme.uri}`} passHref prefetch={false} legacyBehavior>
                    <a className={styles.Tile} style={{background: `linear-gradient(0deg, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.2)), url('${process.env.NEXT_PUBLIC_APP_MEDIA_URL}${scheme.thumbnail_uri}')`, backgroundSize: 'cover', backgroundPosition: `50% 50%` }}
                      onClick={(e) => logInternalLinkClick("Featured Scheme", `/scheme/${scheme.uri}`)}
                    >
                      <div className={styles.TileInner}>
                        <h5>{scheme.name}</h5>
                        <p>{address}</p>
                      </div>
                    </a>
                  </Link>
                );
              })}
            </div>
            <Link href={`/retail-centres/${location}`} passHref prefetch={false} legacyBehavior>
              <a
                onClick={(e) => logInternalLinkClick("View All Schemes Here", `/retail-centres/${location}`)}
                className={`${styles.buttonFont} ${buttons.SecondaryButton}`}
              >
                View All Schemes in {name}
              </a>
            </Link>
          </div>
        )}
        {props.properties && props.properties.length > 0 && (
        <div className={`${styles.FeaturedPicks} ${styles.Properties}`}>
          <h2>Available properties within the area</h2>
          <p>
            We've curated a list of shops to rent in {name}, across highstreet and within larger centres. Why not
            browse our selection today and find a place for your brand?
          </p>
          <div className={`${styles.Tiles} ${styles.PropertiesDiv}`}>
            {props.properties.map((property) => {
              let address = [property.line1 === property.full_name ? null : property.line1, property.line2, property.line3, property.town_name].filter(Boolean).filter(value => Object.keys(value).length !== 0).join(", ");
              address = [address, property.postcode].filter(Boolean).join(", ");
              return (
                <Link
                  key={property.id.raw}
                  href={`${property.scheme_id && property.scheme ? `/scheme/${property.scheme.uri}` : ""}/property/${property.id.raw
                    }`}
                  passHref
                  prefetch={false}
                  legacyBehavior
                >
                  <a className={styles.Tile} style={{background: `linear-gradient(0deg, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.2)), url('${process.env.NEXT_PUBLIC_APP_MEDIA_URL}${property.thumbnail_uri}')`, backgroundSize: 'cover', backgroundPosition: `50% 50%` }}
                    onClick={(e) =>
                      logInternalLinkClick(
                        "Featured Property",
                        `${property.scheme_id && property.scheme ? `/scheme/${property.scheme.uri}` : ""}/property/${property.id.raw
                        }`
                      )
                    }
                  >
                    <div className={styles.TileInner}>
                      <h5>{typeof property.name === 'string' ? property.name : ''}</h5>
                      <p>{address}</p>
                    </div>
                  </a>
                </Link>
              );
            })}
          </div>
          <Link href={`/shops-to-rent/${location}`} passHref prefetch={false} legacyBehavior>
            <a
              onClick={(e) => logInternalLinkClick("View All Properties Here", `/shops-to-rent/${location}`)}
              className={`${styles.buttonFont} ${buttons.SecondaryButton}`}
            >
              View All Properties in {name}
            </a>
          </Link>
        </div>
        )}
      </div>
  );
};

export default DestinationPage;
